/**
 * tokens.ts — Per-family placeholder tokens for the Product Specs editors.
 *
 * The resolver fills these `{token}` placeholders per SKU/variant at render
 * time (e.g. a 250 L tank substitutes {capacity}, {tds}, {wall}…). Authors
 * keep the per-SKU auto-fill by inserting these chips into KPI tiles / spec
 * rows instead of hardcoding a value.
 *
 * Lists are DERIVED from the actual `{placeholder}` occurrences in
 * backend/src/scripts/data/sunstellar-product-specs.ts (verified 2026-07-01) —
 * NOT guessed. Keep in sync if the spec data adds/removes placeholders.
 */

import type { FamilyKey } from '../../../api/productSpecs';

/** Tokens that appear in each family's `spec` (kpiTiles + specRows) templates. */
export const FAMILY_TOKENS: Record<string, string[]> = {
  SS_WATER_TANK: [
    'capacity', 'cap', 'modelName', 'innerMaterial', 'innerThickness',
    'wall', 'tds', 'heatLoss',
  ],
  HOT_WATER_TANK: [
    'capacity', 'cap', 'modelName', 'material', 'insulation',
    'pressure', 'pressureWord', 'workingPressure', 'shellGauge',
  ],
  DOMESTIC_HEAT_PUMP: [
    'model', 'modelLabel', 'modelTag', 'application', 'functions',
    'heatingCapacity', 'cop', 'tankCapacity', 'ratedInputPower', 'ratedCurrent',
    'maxCurrent', 'compressorSize', 'hotWaterProduced', 'heatPumpDimensions',
    'tankDimensions', 'tankHeatExchanger',
  ],
  COMMERCIAL_HEAT_PUMP: [
    'model', 'modelLabel', 'heatingCapacity', 'cop', 'powerSupply',
    'ratedInputPower', 'maxInputPower', 'ratedCurrent', 'maxInputCurrent',
    'compressorSize', 'hotWaterProduced', 'waterCirculation', 'connectionSize',
    'condenserType', 'bodySize', 'netWeight', 'noise',
  ],
  POOL_HEAT_PUMP: [
    'model', 'modelLabel', 'application', 'heatingCapacity', 'heatingCapacityShort',
    'cop', 'copShort', 'powerSupply', 'compressor', 'compressorMake', 'compressorType',
    'heatExchanger', 'refrigerantShort', 'noise', 'noiseShort', 'airThrow',
    'operatingRange', 'operatingCycle', 'installation', 'controls', 'defrost', 'protections',
  ],
  R290: [
    'model', 'modelLabel', 'powerSupply', 'heatingCapacityRange', 'heatingPowerInputRange',
    'coolingCapacityRange', 'coolingPowerInputRange', 'copRange', 'eerRange',
    'heatedWaterOutput', 'waterFlow', 'waterPressureDrop', 'maxCurrent', 'maxPowerInput',
    'fuse', 'wireDiameter', 'pipeDiameter', 'soundPower', 'soundPressure',
    'bodySize', 'netWeight',
  ],
  EVI: [
    'model', 'modelLabel', 'powerSupply', 'heatingCapacity07', 'heatingCapacity20',
    'cop07', 'cop20', 'ratedPower07', 'ratedPower20', 'heatedWaterOutput07',
    'heatedWaterOutput20', 'compressorQty', 'waterCirculation', 'connectionDiameter',
    'tankCompatibility', 'energyCardCapacity', 'productSize', 'noise',
  ],
  SOLAR_WATER_HEATER: [
    'series', 'marketingName', 'innerTankMaterial', 'innerTankSheetThickness',
    'innerTankDishThickness', 'sacrificialProtection', 'pufInsulation', 'outerCladding',
    'supportingStructure', 'solarCollector', 'tubeDiaLength', 'glassMaterial',
    'selectiveCoating', 'absorptance', 'stagnationTemperature', 'referenceDrawing',
    'tdsLimit', 'airVentLimit', 'operatingMode',
  ],
  // Fixed pages carry no per-SKU placeholders today (values are verbatim), but
  // the palette is still shown empty-safe so a future author can add tokens.
  STAINLESS_STEEL_PLUMBING: [],
  HYBRID: [],
  // The datasheet record is fully-resolved per SKU — no placeholders.
  HOT_WATER_TANK_DATASHEETS: [],
};

export function tokensForFamily(key: FamilyKey | string): string[] {
  return FAMILY_TOKENS[key] ?? [];
}
